<script setup lang="ts">
import { SEARCH_INTERPRET_MAX_QUERY_LENGTH } from '@lib/shared';
import lodashShuffle from 'lodash-es/shuffle';

import { type PlatformSellingMethod } from '~/types/sale.type';

// PROPS
interface Props {
    /** From the `useQuickSearch` instance that asks for the tokens: its widget renders here. */
    turnstileContainerId: string;
    isLoading?: boolean;
    interpretPhase?: QuickSearchInterpretPhase;
    /** Shows the "Esc closes" hint — only the modal mount closes on Esc, and only with a keyboard. */
    hasDismissHint?: boolean;
    hasAutofocus?: boolean;
}

const { turnstileContainerId, isLoading = false, interpretPhase = 'none', hasDismissHint = false, hasAutofocus = false } = defineProps<Props>();

// EVENTS
const emit = defineEmits<{
    submit: [];
}>();

// MODELS
const inputValue = defineModel<string>({ required: true });
const sellingMethod = defineModel<PlatformSellingMethod>('sellingMethod', { required: true });

// COMPOSABLES
const { tt } = useTypedI18n();
const isMounted = useMounted();
const reducedMotion = usePreferredReducedMotion();

// DATA
const hintId = useId();
const textarea = useTemplateRef('textarea');
const reading = useTemplateRef('reading');

const placeholderPhrases = computed<string[]>(() => [
    tt('search.quick.placeholder'),
    ...lodashShuffle([
        tt('search.quick.placeholderExamples.coach50Seats'),
        tt('search.quick.placeholderExamples.under500kKm'),
        tt('search.quick.placeholderExamples.cityAutomatic'),
        tt('search.quick.placeholderExamples.intercityRamp'),
        tt('search.quick.placeholderExamples.recentCoachToilet'),
        tt('search.quick.placeholderExamples.setraEuro6'),
        tt('search.quick.placeholderExamples.schoolBusLowKm'),
        tt('search.quick.placeholderExamples.minibusRamp'),
    ]),
]);

const isTypewriterActive = computed<boolean>(() => isMounted.value && reducedMotion.value !== 'reduce' && !inputValue.value && !isLoading);

const { text: typedPlaceholder } = useTypewriter(placeholderPhrases, isTypewriterActive);

// Only an interpreted search waits long enough to be worth telling: codes, VINs and short texts open their listing at once.
const isInterpreting = computed<boolean>(() => interpretPhase !== 'none');

const loadingMessage = computed<string>(() => {
    if (!isInterpreting.value) return tt('search.quick.loading');

    return interpretPhase === 'slow' ? tt('search.quick.phase.slow') : tt('search.quick.phase.reading');
});

// The visible hints are `aria-hidden`, so these two carry the whole assistive channel.
const describedByMessage = computed<string>(() =>
    [tt('search.quick.hint.submit', { key: tt('search.quick.hint.submitKey') }), hasDismissHint ? tt('search.quick.hint.dismiss') : '']
        .filter(Boolean)
        .join(' · ')
);

const statusMessage = computed<string>(() => (isLoading ? loadingMessage.value : ''));

// HANDLERS
// An IME confirming a composition fires Enter too; that must not submit.
const onEnter = (event: KeyboardEvent): void => {
    if (!event.isComposing) emit('submit');
};

// WATCHERS
// Past six rows the field scrolls: the words drawn over it must start from the line it shows.
watch(reading, element => {
    if (element) element.scrollTop = textarea.value?.textareaRef?.scrollTop ?? 0;
});
</script>

<template>
    <div class="relative flex w-full flex-col items-center">
        <QuickSearchMethodTabs v-model="sellingMethod" class="relative z-10" />

        <div class="relative -mt-6 w-full">
            <!-- Behind the card: the turning gradient shows as its border, the blurred copy as the halo around it. -->
            <template v-if="isInterpreting">
                <div
                    aria-hidden="true"
                    class="bg-ai-glow motion-safe:animate-ai-glow pointer-events-none absolute -inset-0.5 rounded-[calc(var(--radius-2xl)+0.125rem)]"
                />
                <div
                    aria-hidden="true"
                    class="bg-ai-glow motion-safe:animate-ai-glow pointer-events-none absolute -inset-1 rounded-[calc(var(--radius-2xl)+0.25rem)] opacity-50 blur-lg"
                />
            </template>

            <div
                class="bg-default relative w-full rounded-2xl p-4 shadow-lg ring-1 md:p-6"
                :class="isInterpreting ? 'ring-transparent' : 'ring-default focus-within:ring-primary'"
            >
                <div class="relative">
                    <UTextarea
                        ref="textarea"
                        v-model="inputValue"
                        :autofocus="hasAutofocus"
                        :rows="2"
                        :maxrows="6"
                        :maxlength="SEARCH_INTERPRET_MAX_QUERY_LENGTH"
                        autoresize
                        autocomplete="off"
                        variant="none"
                        :aria-label="tt('search.quick.aria.input')"
                        :aria-describedby="hintId"
                        :placeholder="tt('search.quick.placeholder')"
                        :ui="{
                            base: [
                                'p-0 pt-4 text-base md:text-lg',
                                isInterpreting && 'text-transparent',
                                isTypewriterActive && 'placeholder:text-transparent',
                            ],
                        }"
                        @keydown.enter.exact.prevent="onEnter"
                    />

                    <!-- The examples are typed over the placeholder, which stays put for screen readers. -->
                    <div
                        v-if="isTypewriterActive"
                        aria-hidden="true"
                        class="text-dimmed pointer-events-none absolute inset-0 overflow-hidden pt-4 text-base break-words whitespace-pre-wrap md:text-lg"
                    >
                        <span>{{ typedPlaceholder }}</span>
                    </div>

                    <!-- The words again, over the field that hides its own, with a light reading through them as the AI does. -->
                    <div
                        v-if="isInterpreting"
                        ref="reading"
                        aria-hidden="true"
                        class="pointer-events-none absolute inset-0 overflow-hidden pt-4 text-base break-words whitespace-pre-wrap md:text-lg"
                    >
                        <span class="bg-ai-reading motion-safe:animate-ai-reading bg-clip-text text-transparent">{{ inputValue }}</span>
                    </div>
                </div>

                <div class="mt-4 flex items-end justify-between gap-4">
                    <div>
                        <!-- Stacked in one cell and toggled with `invisible`: the row keeps the taller hint's height whichever is up. -->
                        <div class="grid">
                            <p
                                class="col-start-1 row-start-1 flex items-center gap-2 text-sm"
                                :class="[isInterpreting ? 'text-default font-medium' : 'text-muted', { invisible: !isLoading }]"
                                aria-hidden="true"
                            >
                                <!-- The negative margin keeps the taller bus from growing the row while the search runs. -->
                                <QuickSearchBusLoader v-if="isInterpreting" :is-uphill="interpretPhase === 'slow'" class="-my-1" />
                                <UIcon v-else name="i-svg-spinners-270-ring" class="size-4 shrink-0" />
                                <Transition
                                    mode="out-in"
                                    enter-active-class="transition-opacity duration-300"
                                    enter-from-class="opacity-0"
                                    leave-active-class="transition-opacity duration-150"
                                    leave-to-class="opacity-0"
                                >
                                    <span v-if="isLoading" :key="loadingMessage">{{ loadingMessage }}</span>
                                </Transition>
                            </p>

                            <p class="text-muted col-start-1 row-start-1 text-sm" :class="{ invisible: isLoading }" aria-hidden="true">
                                <BaseI18nT keypath="search.quick.hint.submit" tag="span">
                                    <template #key>
                                        <span class="text-primary font-medium">{{ tt('search.quick.hint.submitKey') }}</span>
                                    </template>
                                </BaseI18nT>
                                <span v-if="hasDismissHint" class="text-dimmed"> · {{ tt('search.quick.hint.dismiss') }}</span>
                            </p>
                        </div>

                        <span :id="hintId" class="sr-only">{{ describedByMessage }}</span>
                        <div role="status" class="sr-only">{{ statusMessage }}</div>
                    </div>

                    <UButton
                        icon="lucide:arrow-right"
                        square
                        :disabled="isLoading"
                        :aria-label="tt('search.quick.aria.submit')"
                        :class="{ 'motion-safe:animate-pulse': isInterpreting }"
                        :ui="{ base: 'size-14 shrink-0 justify-center rounded-xl' }"
                        @click="emit('submit')"
                    />
                </div>

                <TurnstileChallenge :container-id="turnstileContainerId" />
            </div>
        </div>
    </div>
</template>
